import { MoneyOffRounded as MoneyOffRoundedIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import TimecardTrackingButton from 'pages/timecards/buttons/TimecardTrackingButton';
import { useSelector } from 'store';
import { TimecardDetails } from 'types/timecards';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import Chip from '../@extended/Chip.tsx';
import PartyName from '../party/PartyName';
import TrackingTimer from './TrackingTimer';
import TrackingWidgetControls from './TrackingWidgetControls';

type Props = {
    timecard: TimecardDetails;
    toggleTimecardModal: () => void;
    closeTimecardModal: () => void;
};

const TOGGLE_LINK_CLASS = 'text-fg hover:text-link mt-4 h-auto self-center p-0 font-normal';

export default function TrackingWidget({ timecard, toggleTimecardModal, closeTimecardModal }: Props) {
    const drawerOpen = useSelector((state) => state.menu.drawerOpen);
    const isTimecardCenterOpen = useSelector((state) => state.subDrawer.activeDrawerId === 'timecard-center');
    const toggleTimecardCenter = isTimecardCenterOpen ? closeTimecardModal : toggleTimecardModal;

    return (
        <div
            className={cn(
                // The nav's own background lightened halfway to white, so the widget reads as a card on it.
                'bg-[color-mix(in_srgb,var(--sidebar),var(--card)_50%)]',
                drawerOpen ? 'p-4' : 'p-2'
            )}
        >
            <div className="pointer-events-none invisible absolute">
                <TrackingTimer timecard={timecard} />
            </div>
            {drawerOpen ? (
                <div className="flex flex-col">
                    {timecard.isNonBillable ? (
                        <Chip
                            className="mt-1 cursor-pointer"
                            size="small"
                            label={<MoneyOffRoundedIcon />}
                            type={'partyType'}
                        />
                    ) : null}
                    <p className="text-subtitle2 line-clamp-2 font-bold whitespace-normal">{timecard.description}</p>
                    <p className="text-body2 line-clamp-2 whitespace-normal">{timecard.matter.fullName}</p>
                    <span className="text-caption text-fg-secondary">
                        {timecard.matter.clientParty && (
                            <PartyName party={timecard.matter.clientParty} variant="readonly-other" />
                        )}
                    </span>
                    <TrackingWidgetControls timecard={timecard} />
                    <Button variant="link" className={TOGGLE_LINK_CLASS} onClick={toggleTimecardCenter}>
                        {isTimecardCenterOpen ? t('timecards.hide-timecards') : t('timecards.show-timecards')}
                    </Button>
                </div>
            ) : (
                <div className="flex flex-col pt-2">
                    <TimecardTrackingButton timecard={timecard} />
                    <Button variant="link" className={TOGGLE_LINK_CLASS} onClick={toggleTimecardCenter}>
                        {isTimecardCenterOpen
                            ? t('timecards.hide-timecards-minimal')
                            : t('timecards.show-timecards-minimal')}
                    </Button>
                </div>
            )}
        </div>
    );
}
